import { useMemo } from 'react';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import type { EnvironmentVersions } from '@internal/plugin-waadoo-core';
import { odooTranslationRef } from './translation';
import { OdooVersionBadge } from './OdooVersionBadge';
import { useOdooVersions } from './odooVersions';

/**
 * Les versions d'Odoo des environnements de `project` (`useOdooVersions`),
 * telles que les vues du cœur les montrent (`EnvironmentVersions`) : le
 * badge d'un environnement — après la révision dans la bande de la page
 * d'une branche, après le nom du pod d'application, après la branche dans
 * la carte des environnements — et l'entrée « Version » de l'en-tête du
 * projet. Mémoïsées sur la réponse : une nouvelle identité seulement quand
 * les versions changent, pour les `useMemo` des appelants.
 */
export function useOdooEnvironmentVersions(options: {
  project: string | undefined;
  refreshKey?: unknown;
}): EnvironmentVersions {
  const versions = useOdooVersions(options.project, options.refreshKey);
  const { t } = useTranslationRef(odooTranslationRef);
  return useMemo(
    () => ({
      badge: namespace => {
        const version = namespace ? versions[namespace] : undefined;
        return version ? <OdooVersionBadge version={version} /> : undefined;
      },
      // La version du projet : celle de son environnement dev
      // (`prj-<p>-dev`, la branche que tout projet a), sinon celle du
      // premier environnement qui a répondu ; rien sans réponse.
      headerItem: project => {
        const version =
          versions[`prj-${project}-dev`] ?? Object.values(versions)[0];
        return version
          ? {
              label: t('odooVersion.headerLabel'),
              value: <OdooVersionBadge version={version} />,
            }
          : undefined;
      },
    }),
    [versions, t],
  );
}
